// Abschluss-Feedback und Testimonials der Ausbildungen (seit 03.10.2026: Musik und Sprache).
//
// Ablauf (Daniel): Abschlussquiz bestanden → Feedback (Sterne Pflicht) → erst dann Gesamtzertifikat
// und Zertifikatsmail → danach Sonderangebot, nur ohne Mitgliedschaft.
//
// Datenzugang nur über RPCs und Edge Functions, nicht über Tabellenzugriffe:
//  * Speichern läuft über die Function `ausbildung-feedback-absenden`; die Datenbankfunktion dahinter
//    (`musik_sprache_feedback_speichern`) ist nur für service_role ausführbar, weil sie das
//    Zertifikat ausstellt und den Token für die Admin-Mail setzt.
//  * Öffentlich gibt es nur `ausbildung_bewertungen_oeffentlich()`: Durchschnitt und Anzahl über
//    ALLE Bewertungen, Texte nur freigegeben + mit Einwilligung, und nur Anzeigename, Ort, Sterne, Text.
//
// > `src/integrations/supabase/types.ts` kennt Tabelle und Funktionen erst nach `npm run gen-types`.
// > Bis dahin ist `rufeAuf` der einzige untypisierte Punkt (wie in src/lib/provisionen/db.ts).

import { supabase } from "@/integrations/supabase/client";
import { fehlertextAusEdgeFunction } from "@/lib/edgeFunktionFehler";

export const MUSIK_SPRACHE_LEARNING_AREA_ID = "0844a03b-65ac-4084-8d97-8d1ee70f89e6";

/**
 * Die Section auf der Landingpage erscheint erst ab so vielen freigegebenen Testimonials (Frage D28, Daniel 20.10.2026:
 * „Wir nehmen es auf 2“; vorher drei).
 */
export const MINDESTZAHL_TESTIMONIALS = 2;

/** Gleiche Grenzen wie in der Datenbank und in `_shared/ausbildung-bewertung.ts`. */
export const FEEDBACK_GRENZEN = { text: 500, verbesserung: 2000, name: 60, ort: 80 } as const;

export const EINWILLIGUNG_TEXT =
  "Ich bin einverstanden, dass MusiKonzept meinen Text mit Vornamen und Ort auf der Website zeigt. Ich kann das jederzeit widerrufen.";

export type BewertungsStatus = "neu" | "freigegeben" | "abgelehnt";

export interface OeffentlichesTestimonial {
  anzeigename: string;
  ort: string | null;
  sterne: number;
  text: string;
}

export interface OeffentlicheBewertungen {
  anzahl: number;
  durchschnitt: number | null;
  testimonials: OeffentlichesTestimonial[];
}

export interface MeineBewertung {
  id: string;
  sterne: number;
  status: BewertungsStatus;
  created_at: string;
}

export interface AdminBewertung {
  id: string;
  user_id: string;
  bundle_id: string;
  vorname: string | null;
  nachname: string | null;
  email: string | null;
  sterne: number;
  testimonial_text: string | null;
  einwilligung: boolean;
  anzeigename: string | null;
  ort: string | null;
  verbesserung_text: string | null;
  status: BewertungsStatus;
  entschieden_am: string | null;
  entschieden_per_link: boolean;
  created_at: string;
}

export interface FeedbackFormular {
  sterne: number;
  testimonialText: string;
  einwilligung: boolean;
  anzeigename: string;
  ort: string;
  verbesserungText: string;
}

/** Link-Bewertung, wie die Bestätigungsseite sie sieht. */
export interface LinkBewertung {
  sterne: number;
  text: string | null;
  anzeigename: string | null;
  ort: string | null;
  status: BewertungsStatus;
}

type RpcAntwort = { data: unknown; error: { message: string } | null };
type RpcAufruf = (name: string, args?: Record<string, unknown>) => Promise<RpcAntwort>;

// `bind` ist nötig: ohne Bindung an den Client verliert die Methode ihr `this`.
const rpc = (supabase.rpc as unknown as RpcAufruf).bind(supabase) as RpcAufruf;

async function rufeAuf<T>(name: string, args?: Record<string, unknown>): Promise<T> {
  const { data, error } = await rpc(name, args);
  if (error) throw new Error(error.message);
  return data as T;
}

async function functionAufrufen<T>(name: string, body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke(name, { body });
  if (error) throw new Error(await fehlertextAusEdgeFunction(error));
  return data as T;
}

/**
 * Prüft das Formular vor dem Absenden. Gibt den ersten Fehler als Satz zurück, sonst null.
 * Die Function und die Datenbank prüfen dasselbe noch einmal.
 */
export function feedbackFormularFehler(f: FeedbackFormular): string | null {
  if (!Number.isInteger(f.sterne) || f.sterne < 1 || f.sterne > 5) return "Bitte vergib 1 bis 5 Sterne.";
  const text = f.testimonialText.trim();
  if (text.length > FEEDBACK_GRENZEN.text) return `Dein Text ist zu lang (höchstens ${FEEDBACK_GRENZEN.text} Zeichen).`;
  if (f.verbesserungText.trim().length > FEEDBACK_GRENZEN.verbesserung) {
    return `Dein Verbesserungsvorschlag ist zu lang (höchstens ${FEEDBACK_GRENZEN.verbesserung} Zeichen).`;
  }
  if (text && f.einwilligung && !f.anzeigename.trim()) {
    return "Bitte gib deinen Vornamen an, damit wir deinen Text zeigen können.";
  }
  if (f.anzeigename.trim().length > FEEDBACK_GRENZEN.name) return "Der Name ist zu lang.";
  if (f.ort.trim().length > FEEDBACK_GRENZEN.ort) return "Der Ort ist zu lang.";
  return null;
}

/** Was an die Function geht. Ohne Text gibt es keine Einwilligung, ohne Einwilligung weder Name noch Ort. */
export function feedbackNutzlast(f: FeedbackFormular) {
  const text = f.testimonialText.trim();
  const einwilligung = !!text && f.einwilligung;
  return {
    sterne: f.sterne,
    testimonial_text: text || null,
    einwilligung,
    anzeigename: einwilligung ? f.anzeigename.trim() || null : null,
    ort: einwilligung ? f.ort.trim() || null : null,
    verbesserung_text: f.verbesserungText.trim() || null,
  };
}

/** Section auf der Landingpage erst ab MINDESTZAHL_TESTIMONIALS freigegebenen Texten. */
export function zeigeBewertungsAbschnitt(d: OeffentlicheBewertungen | null | undefined): d is OeffentlicheBewertungen {
  return !!d && d.testimonials.length >= MINDESTZAHL_TESTIMONIALS && d.anzahl > 0 && d.durchschnitt !== null;
}

/** 4.8 → „4,8“ */
export function durchschnittText(durchschnitt: number): string {
  return Number(durchschnitt).toLocaleString("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

export const bewertungenDb = {
  oeffentlich: (bundleId: string) =>
    rufeAuf<OeffentlicheBewertungen>("ausbildung_bewertungen_oeffentlich", { p_bundle_id: bundleId }),

  meine: (bundleId: string) =>
    rufeAuf<MeineBewertung | null>("meine_ausbildung_bewertung", { p_bundle_id: bundleId }),

  adminListe: (bundleId?: string | null) =>
    rufeAuf<AdminBewertung[]>("ausbildung_bewertungen_admin_liste", { p_bundle_id: bundleId ?? null }),

  /** Admin: „neu“ = Zurückziehen (nicht mehr öffentlich, wieder offen). */
  entscheiden: (id: string, status: BewertungsStatus) =>
    rufeAuf<void>("ausbildung_bewertung_entscheiden", { p_id: id, p_status: status }),

  feedbackAbsenden: (f: FeedbackFormular) =>
    functionAufrufen<{ ok: true; zertifikat_neu: boolean; certificate_id: string | null }>(
      "ausbildung-feedback-absenden",
      feedbackNutzlast(f),
    ),

  linkLesen: (token: string) =>
    functionAufrufen<{ gueltig: boolean; bewertung?: LinkBewertung }>("ausbildung-bewertung-entscheiden", {
      token,
      aktion: "lesen",
    }),

  linkEntscheiden: (token: string, aktion: "freigeben" | "ablehnen") =>
    functionAufrufen<{ gueltig: boolean; ergebnis?: { status: BewertungsStatus } }>(
      "ausbildung-bewertung-entscheiden",
      { token, aktion },
    ),
};
